// NextStepBanner (Spec §6.2 / P3) — on workflow screens, tells the user the one
// recommended next action so the system sequences the work and the path is
// always visible. Rendered by PageHeader's `banner` slot.

import type { ReactNode } from 'react'
import { ArrowRight, Sparkles } from 'lucide-react'

interface NextStepBannerProps {
  /** what to do next, in plain language */
  message: ReactNode
  /** optional label for the pipeline stage the user is in */
  stage?: string
  /** the call-to-action button/link */
  action?: ReactNode
  tone?: 'info' | 'success'
}

export default function NextStepBanner({ message, stage, action, tone = 'info' }: NextStepBannerProps) {
  const styles =
    tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : 'border-iflow-200 bg-iflow-50 text-iflow-900'
  return (
    <div className={`flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${styles}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-iflow-600">
          <Sparkles size={18} aria-hidden />
        </span>
        <div>
          {stage && <p className="text-xs font-semibold uppercase tracking-wide text-iflow-600">{stage}</p>}
          <p className="text-sm leading-relaxed">{message}</p>
        </div>
      </div>
      {action && <div className="shrink-0 sm:ml-4">{action}</div>}
    </div>
  )
}

/** Small right-arrow-styled inline CTA text, for use inside `action`. */
export function NextStepArrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 font-medium">
      {children}
      <ArrowRight size={15} aria-hidden />
    </span>
  )
}
